import { useState, useEffect } from "react";
import { useCustomerAuth } from "@/components/customer/CustomerAuthProvider";
import { CustomerPortalLayout } from "@/components/customer/CustomerPortalLayout";
import { LanguagePrintDialog } from "@/components/invoices/LanguagePrintDialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Receipt, CreditCard, Calendar, FileText, Clock, StickyNote } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocalization } from "@/hooks/useLocalization";
import { toast } from "sonner";
import { formatPeopleDate, formatDateWithA11y } from "@/lib/dateUtils";

interface Payment {
  id: string;
  receipt_number: number;
  amount_gross: number;
  payment_method: string;
  paid_at: string;
  created_at: string;
  notes?: string;
  invoice: {
    id: string;
    invoice_number: string;
    total_gross: number;
    amount_paid: number;
  };
}

export default function CustomerPayments() {
  const { customer } = useCustomerAuth();
  const { t } = useLocalization();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [printDialogState, setPrintDialogState] = useState<{paymentId: string} | null>(null);

  useEffect(() => {
    if (customer?.email) {
      fetchPayments();
    }
  }, [customer]);

  const fetchPayments = async () => {
    if (!customer?.email) return;

    try {
      setLoading(true);
      
      // Get payments for this customer's invoices
      const { data, error } = await supabase
        .from('payments')
        .select(`
          id,
          receipt_number,
          amount_gross,
          payment_method,
          paid_at,
          created_at,
          notes,
          invoices!inner(
            id,
            invoice_number,
            total_gross,
            amount_paid,
            orders!inner(
              customers!inner(
                email
              )
            )
          )
        `)
        .eq('invoices.orders.customers.email', customer.email)
        .order('paid_at', { ascending: false });

      if (error) {
        console.error('Error fetching payments:', error);
        toast.error(t('payments.customer.loadError'));
        return;
      }

      // Transform the data to match our interface
      const transformedPayments: Payment[] = data.map(payment => ({
        id: payment.id,
        receipt_number: payment.receipt_number,
        amount_gross: payment.amount_gross,
        payment_method: payment.payment_method,
        paid_at: payment.paid_at,
        created_at: payment.created_at,
        notes: payment.notes || undefined,
        invoice: {
          id: payment.invoices.id,
          invoice_number: payment.invoices.invoice_number,
          total_gross: payment.invoices.total_gross,
          amount_paid: payment.invoices.amount_paid
        }
      }));

      setPayments(transformedPayments);
    } catch (error) {
      console.error('Error fetching payments:', error);
      toast.error(t('payments.customer.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReceipt = async (paymentId: string, locale?: string) => {
    try {
      const url = new URL(`https://zsbvxxdmhewaxkbkqgga.supabase.co/functions/v1/generate-payment-receipt/${paymentId}`);
      if (locale) {
        url.searchParams.set('locale', locale);
      }
      
      const { data: session } = await supabase.auth.getSession();
      
      const response = await fetch(url.toString(), {
        headers: {
          'Authorization': `Bearer ${session.session?.access_token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const htmlContent = await response.text();
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const receiptUrl = URL.createObjectURL(blob);
      window.open(receiptUrl, '_blank');
      
      setTimeout(() => URL.revokeObjectURL(receiptUrl), 1000);
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error(t('payments.customer.downloadError'));
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card':
      case 'credit_card':
        return <CreditCard className="h-4 w-4" />;
      default:
        return <Receipt className="h-4 w-4" />;
    }
  };

  const getPaymentMethodDisplay = (method: string) => {
    switch (method.toLowerCase()) {
      case 'card':
        return t('payments.method.creditCard');
      case 'cash':
        return t('payments.method.cash');
      case 'bank_transfer':
        return t('payments.method.bankTransfer');
      default:
        return method.charAt(0).toUpperCase() + method.slice(1);
    }
  };

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/3 mb-6"></div>
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-32 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('payments.paymentHistory')}</h1>
          <p className="text-muted-foreground">
            {t('payments.customer.subtitle')}
          </p>
        </div>

        {payments.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('payments.noPayments')}</h3>
              <p className="text-muted-foreground text-center">
                {t('payments.noPaymentsDescription')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {payments.map((payment) => (
              <Card key={payment.id} className="overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                        {getPaymentMethodIcon(payment.payment_method)}
                      </div>
                      <div>
                        <CardTitle className="text-lg">
                          {t('payments.receiptNumber')}{payment.receipt_number}
                        </CardTitle>
                        <CardDescription>
                          {t('payments.customer.forInvoice', { invoice: payment.invoice.invoice_number })}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-primary">
                        €{payment.amount_gross.toFixed(2)}
                      </div>
                      <Badge variant="secondary" className="mt-1">
                        {getPaymentMethodDisplay(payment.payment_method)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center space-x-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{t('payments.customer.paymentMade')}:</span>
                        <div className="font-medium">
                          <time 
                            dateTime={formatDateWithA11y(payment.paid_at).ariaLabel}
                            title={formatDateWithA11y(payment.paid_at).ariaLabel}
                          >
                            {formatPeopleDate(payment.paid_at, { relative: true, includeTime: true })}
                          </time>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{t('payments.customer.recordCreated')}:</span>
                        <div className="font-medium">
                          <time 
                            dateTime={formatDateWithA11y(payment.created_at).ariaLabel}
                            title={formatDateWithA11y(payment.created_at).ariaLabel}
                          >
                            {formatPeopleDate(payment.created_at, { relative: true, includeTime: true })}
                          </time>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{t('receipt.invoiceTotal')}:</span>
                        <span className="font-medium">
                          €{payment.invoice.total_gross.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 text-sm">
                      <Receipt className="h-4 w-4 text-muted-foreground" />
                      <div className="flex flex-col">
                        <span className="text-muted-foreground">{t('payments.remaining')}:</span>
                        <span className="font-medium">
                          €{(payment.invoice.total_gross - payment.invoice.amount_paid).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {payment.notes && (
                    <div className="mb-4 p-3 bg-muted/50 rounded-lg">
                      <div className="flex items-start space-x-2 text-sm">
                        <StickyNote className="h-4 w-4 text-muted-foreground mt-0.5" />
                        <div>
                          <span className="text-muted-foreground font-medium">{t('payments.notes')}:</span>
                          <p className="text-foreground mt-1">{payment.notes}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPrintDialogState({paymentId: payment.id})}
                      className="flex items-center space-x-2"
                    >
                      <Receipt className="h-4 w-4" />
                      <span>{t('payments.downloadReceipt')}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      <LanguagePrintDialog
        open={!!printDialogState}
        onOpenChange={(open) => !open && setPrintDialogState(null)}
        title={t('invoices.printReceipt')}
        icon="receipt"
        onPrint={(locale) => {
          if (printDialogState) {
            handleDownloadReceipt(printDialogState.paymentId, locale);
          }
        }}
      />
    </CustomerPortalLayout>
  );
}
